// src/components/PdiNotesModal.js
// PDI / Shop notes modal — append-only timeline of shop-floor notes attached
// directly to an inventory record (stored as JSONB on the record itself).

import React, { useCallback, useEffect, useState } from "react";
import { api } from "../scripts/api";
import Modal from "./Modal";
import { ClipboardList, Send, Trash2 } from "lucide-react";

export default function PdiNotesModal({ table, recordId, recordLabel, onClose }) {
  const [notes,   setNotes]   = useState([]);
  const [body,    setBody]    = useState("");
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/inventory/${table}/${recordId}/pdi-notes`);
      setNotes(Array.isArray(data?.notes) ? data.notes : []);
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to load notes");
    } finally {
      setLoading(false);
    }
  }, [table, recordId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const text = String(body || "").trim();
    if (!text) { setErr("Enter a note."); return; }
    setSaving(true); setErr("");
    try {
      const { data } = await api.post(`/inventory/${table}/${recordId}/pdi-notes`, { body: text });
      setNotes(Array.isArray(data?.notes) ? data.notes : []);
      setBody("");
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to save note");
    } finally {
      setSaving(false);
    }
  };

  const del = async (idx) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      const { data } = await api.delete(`/inventory/${table}/${recordId}/pdi-notes/${idx}`);
      setNotes(Array.isArray(data?.notes) ? data.notes : []);
    } catch (e) {
      setErr(e?.response?.data?.error || "Failed to delete note");
    }
  };

  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") save();
  };

  // Newest first in the displayed list
  const sorted = [...notes].map((n, i) => ({ ...n, _idx: i })).reverse();

  return (
    <Modal onClose={onClose}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <ClipboardList size={20} style={{ color: "#0891b2" }} />
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>PDI / Shop Notes</h2>
          {recordLabel && (
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              {recordLabel}
            </div>
          )}
        </div>
      </div>

      {err && (
        <div className="card" style={{ marginBottom: 12, color: "#dc2626", fontSize: 13, padding: 10 }}>
          {err}
        </div>
      )}

      <div style={{ maxHeight: 360, overflowY: "auto", display: "grid", gap: 8, marginBottom: 14 }}>
        {loading && <div className="muted" style={{ fontSize: 13 }}>Loading…</div>}
        {!loading && sorted.length === 0 && (
          <div className="muted" style={{ fontSize: 13 }}>
            No notes yet. Add the first one below.
          </div>
        )}
        {sorted.map((n) => (
          <div
            key={n._idx}
            className="card"
            style={{ padding: 10, background: "rgba(8,145,178,0.06)", border: "1px solid rgba(8,145,178,0.25)" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{n.by || "unknown"}</span>
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="muted" style={{ fontSize: 11 }}>
                  {n.at ? new Date(n.at).toLocaleString() : ""}
                </span>
                <button
                  type="button"
                  onClick={() => del(n._idx)}
                  title="Delete note"
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "#dc2626", padding: 2, display: "inline-flex", alignItems: "center",
                  }}
                >
                  <Trash2 size={13} />
                </button>
              </span>
            </div>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 13, color: "var(--fg)" }}>
              {n.body}
            </div>
          </div>
        ))}
      </div>

      <div style={{ paddingTop: 12, borderTop: "1px solid var(--card-border)" }}>
        <textarea
          className="input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="New shop / PDI note… (Ctrl+Enter to save)"
          style={{ width: "100%", resize: "vertical", fontSize: 14 }}
          onKeyDown={onKeyDown}
          disabled={saving}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
          <button className="btn-outline" onClick={onClose} disabled={saving}>Close</button>
          <button
            className="btn"
            onClick={save}
            disabled={saving || !body.trim()}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Send size={14} />
            {saving ? "Saving…" : "Add Note"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
